/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  console.log('🔧 AuthProvider state:', { user: user?.email, userProfile, organization, loading, initialized });

  useEffect(() => {
    // Only run on client-side after component mounts
    if (typeof window === 'undefined') {
      console.log('⚠️ Server-side render, skipping auth');
      return;
    }

    let mounted = true;
    
    console.log('🚀 Client-side auth initialization...');
    
    // Small delay to ensure we're fully client-side
    setTimeout(() => {
      if (!mounted) return;
      
      console.log('🔍 Setting up auth listener...');
      
      // Only use onAuthStateChange - don't call getUser or getSession
      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('🔔 Auth event:', event, session ? session.user.email : 'No session');
        
        if (!mounted) return;
        
        if (session?.user) {
          setUser(session.user);
          await loadUserProfile(session.user.id, session.access_token);
        } else {
          setUser(null);
          setUserProfile(null);
          setOrganization(null);
          if (initialized) {
            setLoading(false);
          }
        }
        
        if (!initialized) {
          setInitialized(true);
          setLoading(false);
        }
      });
      
      console.log('✅ Auth listener set up');
      
      // Trigger the initial event by checking localStorage
const savedSession = localStorage.getItem('sb-bfffxyrfgvgprxjgqrvf-auth-token');
if (savedSession) {
  console.log('💾 Found saved session in localStorage');
} else {
  console.log('💾 No saved session');
  setLoading(false);
  setInitialized(true);
}

// FAILSAFE: Force initialization after 3 seconds
setTimeout(() => {
  if (!initialized) {
    console.log('⏱️ Timeout - forcing initialized to true');
    setInitialized(true);
    setLoading(false);
  }
}, 3000);
      
    }, 100); // 100ms delay to ensure client-side

    return () => {
      mounted = false;
    };
  }, []);

  const loadUserProfile = async (userId, accessToken) => {
  if (userProfile?.id === userId) {
    console.log('Profile already loaded, skipping...');
    return;
  }
  
  console.log('Loading profile for user:', userId);
  
  try {
    // Use direct fetch instead of Supabase client
    console.log('🔍 Using direct fetch for profile...');

    // Use the signed-in user's token so row-level security recognises them
    // (falls back to the public key only if no session token is available)
    const authToken = accessToken || process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    const profileResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/user_profiles?id=eq.${userId}&select=*`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${authToken}`
        }
      }
    );
    
    const profileData = await profileResponse.json();
    console.log('📊 Profile fetch result:', profileData);
    
    if (!profileData || profileData.length === 0) {
      throw new Error('No profile found');
    }
    
    const profile = profileData[0];
    setUserProfile(profile);
    
    // Load organization with direct fetch
    if (profile.organization_id) {
      console.log('🔍 Fetching organization...');
      const orgResponse = await fetch(
        `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/organizations?id=eq.${profile.organization_id}&select=*`,
        {
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      const orgData = await orgResponse.json();
      console.log('📊 Organization fetch result:', orgData);
      
      if (orgData && orgData.length > 0) {
        setOrganization(orgData[0]);
        console.log('✅ Organization loaded:', orgData[0].name);
      }
    }
    
    console.log('✅ Profile loaded successfully');
  } catch (error) {
    console.error('❌ Profile load error:', error);
  }
};

  const signUp = async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signInWithMagicLink = async (email) => {
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Magic link error:', error);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setUserProfile(data);
      return { data, error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { data: null, error };
    }
  };

  const value = {
    user,
    userProfile,
    organization,
    loading,
    initialized,
    signUp,
    signIn,
    signInWithMagicLink,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;